/* Verbatim port of the generic string/URL helpers from
 * service/review/index.html (~851-882). Porting rules (Task F1 brief):
 * function bodies copied EXACTLY; only added type annotations. (Batch C:
 * the original loose `any` annotations became honest types — `unknown`
 * for stringify-anything helpers, `string | null | undefined` for the URL
 * helpers, a type-preserving generic for fixMojibake.)
 *
 * urlNorm is the one sanctioned NEW function (F1 brief step 4), unifying
 * domainOf (~852) and the old mvNorm (~2099) — added only after
 * hub/tests/unit/urlnorm.test.ts's equivalence tests (harvested from the
 * real fixtures) were green against private copies of both originals.
 * domainOf and mvNorm disagree on any URL with a path (domainOf keeps only
 * the hostname; mvNorm keeps the full lowercased path minus protocol/www/
 * fragment/trailing-slash) and on unparseable input (domainOf -> null,
 * mvNorm -> '' or the input lowercased) — see urlnorm.test.ts's
 * "disagreement class" tests. urlNorm(x, {mode}) preserves EACH call
 * site's exact original behavior rather than picking one.
 */

import type { FactEntry } from '../types/contract.ts';

// Full HTML-entity escaper (CTO gate condition 3, F2R): & < > " ' — in that
// order (& must go first so the entities it introduces aren't re-escaped).
// Covers text-content, double-quoted-attribute, and single-quoted-attribute
// breakout (see hub/tests/unit/esc-injection.test.ts).
//
// F3c END-STATE (grep-audited repo-wide): every lit-html view/ws/ui module
// this migration converts has dropped esc() — lit escapes child/attribute
// bindings itself. esc() is NOT deletable, though: it still has real,
// necessary consumers outside the lit-converted surface —
//   - lib/format.ts's own favForDomain() (below).
//   - views/markdown.ts's renderMarkdown() pipeline (deliberately NOT
//     lit-converted per the F3a brief — it's a pure string->string
//     transform with no DOM touch at all) and views/dossier.ts's
//     unsafeHTML(renderMarkdown(...)) consumer of that pipeline's output.
//   - derive/entity.ts's planeVerdicts() — a genuinely computational use
//     (escaping a peer brand's name into a plain-text `.read` narrative
//     field), not template-building.
//   - ui/graph.ts — the §4 relationship graph, explicitly NOT lit-converted
//     (F3b's brief: "stays fully imperative"); still builds SVG markup via
//     raw template strings.
//   - ui/runModal.ts's #progBox streaming internals (poll-tick log feed) —
//     deliberately left as a raw-imperative island this wave (see that
//     file's doc comment: incremental/scroll-preserving append, not a
//     lit-render()-friendly shape); esc() is still the correct escaping
//     mechanism for the server-controlled research-agent log text it
//     innerHTML's there.
//   (hub/src/portfolio.ts used to be on this list — its Batch E lit
//   conversion dropped esc() like every other converted page.)
export function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export type UrlNormMode = 'domain' | 'full';

/** Unifies domainOf (mode:'domain', the default) and the old mvNorm
 * (mode:'full'). Each mode's body is the verbatim original — see the
 * module doc comment above for why both are kept rather than merged. */
export function urlNorm(u: string | null | undefined, opts?: { mode?: UrlNormMode }): string | null {
  const mode = opts?.mode ?? 'domain';
  if (mode === 'full') {
    // verbatim mvNorm (~2099)
    return ((u||'').toLowerCase().replace(/^https?:\/\//,'').replace(/^www\./,'')
      .split('#')[0] ?? '').replace(/\/+$/,'');
  }
  // verbatim domainOf (~852)
  try { return new URL(u ?? '').hostname.replace(/^www\./, ''); } catch { return null; }
}

export function domainOf(u: string | null | undefined): string | null {
  return urlNorm(u, { mode: 'domain' });
}

export function faviconUrl(u: string | null | undefined, sz?: number): string {
  const d = domainOf(u); return d ? `https://www.google.com/s2/favicons?domain=${d}&sz=${sz || 64}` : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
}

export function favForDomain(d: string | null | undefined, sz?: number): string {
  return `https://www.google.com/s2/favicons?domain=${esc(d)}&sz=${sz || 32}`;
}

/* human list join: "a", "a and b", "a, b and c" — long location lists were
   rendering as "LA and Boston and Chicago and Miami and Houston" */
export function listJoin(arr: readonly string[] | null | undefined): string {
  arr = arr || []; return arr.length <= 1 ? (arr[0] || '')
    : arr.length === 2 ? arr.join(' and ')
    : arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
}

/** Type-preserving passthrough: non-mojibake input (or any falsy value)
 * comes back unchanged, so the generic carries the caller's type through.
 * String(s)/`as T` are exactly the coercions the untyped original relied
 * on implicitly (RegExp.test and escape() both stringify their argument). */
export function fixMojibake<T>(s: T): T {
  if (!s || !/â|Ã.|ð|Â/.test(String(s))) return s;
  try { return decodeURIComponent(escape(String(s))) as T; } catch { return s; }
}

/* Sentence-case a short label (first letter up, rest untouched) — used for the
 * priority pills now the archetype dropped text-transform:uppercase (P1.1). */
export function cap(s: unknown): string {
  const t = String(s ?? ''); return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

export function slugify(t: unknown): string {
  return String(t || '').toLowerCase().replace(/https?:\/\//, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'x';
}

/* chip() REMOVED (F3c end-state cleanup): the string-returning original
 * (`text ? \`<span class="chip ${cls}">${esc(text)}</span>\` : ''`) had its
 * last two call sites — views/search.ts (via lib/glossary.ts's tip(), a
 * separate function) and views/timeline.ts's renderKeyDomains — convert to
 * lib/templates.ts's chipT() TemplateResult twin in this wave's step 1-2
 * commits (grep-confirmed zero remaining `chip(` call sites repo-wide).
 * chipT() (lib/templates.ts) is the live implementation now; every other
 * view converted in F3a/F3b already used chipT() exclusively. */

/* NOT explicitly listed in the F1 brief's lib/format.ts module map, but
 * pulled in here because it is a tiny, dependency-free formatting helper
 * (originally ~880, alongside canEdit/factVal/factSrc) needed by
 * derive/entity.ts's planeVerdicts(). Verbatim body. */
export function pct(x: number | null | undefined): number {
  return Math.round((x || 0) * 100);
}

/* F1 left factVal/factSrc unported (service/review/index.html ~878-879,
 * alongside canEdit/pct in the same generic-helpers block) — they're added
 * here now because F2a's renderExec (~1390-1567) reads both directly for
 * the "brand-presence hub band" facts list (`ent.facts`). Verbatim bodies. */
export function factVal(v: FactEntry | undefined): string | null {
  return v && typeof v === 'object' ? v.value : (v ?? null);
}

export function factSrc(v: FactEntry | undefined): string | null {
  return v && typeof v === 'object' ? v.source : null;
}
